import PROCTORING_ERRORS from '../enums/proctoringErrors.js'

const getBrowserRegexMatches = sanitizedUserAgent => {
    return (
        /(edge)\/([\w.]+)/.exec(sanitizedUserAgent) ||
        /(edg)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(opr)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(opt)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(fxios)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(edgios)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(jsdom)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(samsungbrowser)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(electron)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(chrome)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(crios)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(opios)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(version)(applewebkit)[/]([\w.]+).*(safari)[/]([\w.]+)/.exec(
            sanitizedUserAgent
        ) ||
        /(webkit)[/]([\w.]+).*(version)[/]([\w.]+).*(safari)[/]([\w.]+)/.exec(
            sanitizedUserAgent
        ) ||
        /(applewebkit)[/]([\w.]+).*(safari)[/]([\w.]+)/.exec(
            sanitizedUserAgent
        ) ||
        /(webkit)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(opera)(?:.*version|)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        /(msie) ([\w.]+)/.exec(sanitizedUserAgent) ||
        /(fennec)[/]([\w.]+)/.exec(sanitizedUserAgent) ||
        (sanitizedUserAgent.indexOf('trident') >= 0 &&
            /(rv)(?::| )([\w.]+)/.exec(sanitizedUserAgent)) ||
        (sanitizedUserAgent.indexOf('compatible') < 0 &&
            /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(sanitizedUserAgent)) ||
        []
    )
}

const getHardwareErrorMessage = async (error) => {
    const stopStreamTracks = stream => {
        stream?.getTracks?.().forEach(track => track.stop())
    }

    const getPermissionState = async name => {
        try {
            const permission = await navigator.permissions.query({ name })
            return permission.state
        } catch (e) {
            console.debug(`Permissions API not fully supported for ${name}`, e)
            return 'unknown'
        }
    }

    const probeMediaAccess = async constraints => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints)
            stopStreamTracks(stream)
            return { ok: true, error: null }
        } catch (probeError) {
            return { ok: false, error: probeError }
        }
    }

    const determineDeviceState = async (probe, name) => {
        if (probe.ok) return 'granted'

        const isDenied =
            probe.error?.name === 'NotAllowedError' ||
            probe.error?.message === PROCTORING_ERRORS.PERMISSION_DENIED ||
            probe.error?.message === PROCTORING_ERRORS.PERMISSION_DENIED_BY_SYSTEM

        return isDenied ? 'denied' : await getPermissionState(name)
    }

    const getPermissionMessage = (
        cameraState,
        microphoneState,
        isSystemDenied
    ) => {
        const suffix = isSystemDenied
            ? '. Please check your Privacy & Security settings and try again.'
            : ' for this browser tab and try again.'
        const prep = isSystemDenied ? 'access the ' : 'access '

        if (
            cameraState === 'denied' &&
            microphoneState === 'denied'
        ) {
            return `Please enable permissions to ${prep}Camera & Microphone${suffix}`
        }
        if (cameraState === 'denied') {
            return `Please enable permissions to ${prep}Camera${suffix}`
        }
        if (microphoneState === 'denied') {
            return `Please enable permissions to ${prep}Microphone${suffix}`
        }

        return null
    }

    try {
        if (error.message === PROCTORING_ERRORS.DEVICE_NOT_FOUND) {
            const devices = await navigator.mediaDevices.enumerateDevices()
            const hasCamera = devices.some(device => device.kind === 'videoinput')
            const hasMic = devices.some(device => device.kind === 'audioinput')

            if (!hasCamera && !hasMic) {
                return "It looks like both camera and microphone are not functioning/don't exist. Kindly ensure your device has working camera and microphone and try again."
            }
            if (!hasCamera) {
                return "It looks like your camera is not functioning/it doesn't exist. Kindly ensure your device has a working camera and try again."
            }
            if (!hasMic) {
                return "It looks like your microphone is not functioning/it doesn't exist. Kindly ensure your device has a working microphone and try again."
            }
        }

        if (error.message === PROCTORING_ERRORS.PERMISSION_DENIED || error.message === PROCTORING_ERRORS.PERMISSION_DENIED_BY_SYSTEM) {
            const isSystemDenied = error.message === PROCTORING_ERRORS.PERMISSION_DENIED_BY_SYSTEM
            const cameraProbe = await probeMediaAccess({
                video: true,
                audio: false,
            })
            const microphoneProbe = await probeMediaAccess({
                video: false,
                audio: true,
            })
            const cameraState = await determineDeviceState(cameraProbe, 'camera')
            const microphoneState = await determineDeviceState(
                microphoneProbe,
                'microphone'
            )

            const permissionMessage = getPermissionMessage(
                cameraState,
                microphoneState,
                isSystemDenied
            )

            if (permissionMessage) {
                return permissionMessage
            }
        }
    } catch (e) {
        console.error('Error in getHardwareErrorMessage', e)
    }

    return error.message
}

const getRecordingErrorMessage = async error => {
    switch (error) {
        case 'permission_denied':
            return getHardwareErrorMessage({
                message: PROCTORING_ERRORS.PERMISSION_DENIED,
            })
        case 'no_specified_media_found':
            return getHardwareErrorMessage({
                message: PROCTORING_ERRORS.DEVICE_NOT_FOUND,
            })
        case 'media_in_use':
            return 'Your camera or microphone is already in use by another application. Please close it and try again.'
        case 'media_aborted':
            return 'Camera or microphone access was interrupted. Please check your device and try again.'
        case 'invalid_media_constraints':
            return 'Your camera or microphone does not support the required recording settings.'
        case 'no_constraints':
            return 'Camera and microphone access could not be requested. Please refresh the page and try again.'
        case 'recorder_error':
            return 'Video recording failed. Please check your camera and microphone and try again.'
        default:
            return 'Video recording failed. Please try again.'
    }
}

export {
    getBrowserRegexMatches,
    getHardwareErrorMessage,
    getRecordingErrorMessage,
}
